<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Logo from 'next/icon/Logo.vue';
import LabelValueRow from './components/LabelValueRow.vue';
import BlockBar from './components/BlockBar.vue';
import QueueGauge from './components/QueueGauge.vue';
import MultiSelectFilter from './components/MultiSelectFilter.vue';
import TimeRangeSelector from './components/TimeRangeSelector.vue';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import { getUnixStartOfDay } from 'helpers/DateHelper';
import liveReports from 'dashboard/api/liveReports';
import summaryReportsApi from 'dashboard/api/summaryReports';
import { formatFriendlyDuration } from './durationFormat';
import { weightedAvgResolutionTime, filterByIds } from './teamSummaryMetrics';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const props = defineProps({
  inboxId: { type: [Number, String], default: null },
  teamIds: { type: Array, default: () => [] },
});

const { t } = useI18n();
const { accountId } = useAccount();

const SECONDS_IN_DAY = 24 * 60 * 60;

// Quantidade de itens marcados como selecionados por padrão ao carregar.
const DEFAULT_ITEMS_LIMIT = 5;

// Mesma convenção do seletor de time do header (Index.vue): 0 representa
// "sem time" no filtro global.
const NO_TEAM_ID = 0;

// Linha "Sem tag" devolvida pelo summary_reports/label com include_unlabeled
// — vem sem nome do backend, o nome sai do i18n.
const NO_LABEL_ID = 0;

const store = useStore();

// Filtros (multi-select e período) ficam em cache no localStorage por conta
// pra não resetar quando o usuário sai da tela e volta — é só cache local no
// navegador, não é salvo no backend.
const savedFilters =
  LocalStorage.getFromJsonStore(
    LOCAL_STORAGE_KEYS.REPORT_DASHBOARD_OPERATIONAL_FILTERS,
    accountId.value
  ) || {};

const waitingToday = ref(0);
const waitingYesterday = ref(0);
const teamConversationMetric = ref([]);
const totalFinalizadosHoje = ref(0);
const avgResolutionTime24h = ref('');
const avgResolutionTimeWeekly = ref('');
const selectedLabelIds = ref(savedFilters.selectedLabelIds || []);
const selectedOpenLabelIds = ref(savedFilters.selectedOpenLabelIds || []);
const selectedAgentIds = ref(savedFilters.selectedAgentIds || []);
const selectedAvgTimeLabelIds = ref(savedFilters.selectedAvgTimeLabelIds || []);
const agentRange = ref(savedFilters.agentRange || '24h');
const labelRange = ref(savedFilters.labelRange || '24h');
// Se já existe seleção em cache pra essa conta, não aplica os defaults (5
// primeiros) — a última escolha do usuário prevalece.
const labelDefaultsApplied = ref(Boolean(savedFilters.selectedLabelIds));
const openLabelDefaultsApplied = ref(
  Boolean(savedFilters.selectedOpenLabelIds)
);
const agentDefaultsApplied = ref(Boolean(savedFilters.selectedAgentIds));

const persistFilters = () => {
  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.REPORT_DASHBOARD_OPERATIONAL_FILTERS,
    accountId.value,
    {
      selectedLabelIds: selectedLabelIds.value,
      selectedOpenLabelIds: selectedOpenLabelIds.value,
      selectedAgentIds: selectedAgentIds.value,
      selectedAvgTimeLabelIds: selectedAvgTimeLabelIds.value,
      agentRange: agentRange.value,
      labelRange: labelRange.value,
    }
  );
};

watch(
  [
    selectedLabelIds,
    selectedOpenLabelIds,
    selectedAgentIds,
    selectedAvgTimeLabelIds,
    agentRange,
    labelRange,
  ],
  persistFilters,
  { deep: true }
);

const labelSummaryReports = computed(
  () => store.getters['summaryReports/getLabelSummaryReports']
);
const agentSummaryReports = computed(
  () => store.getters['summaryReports/getAgentSummaryReports']
);
const agents = computed(() => store.getters['agents/getAgents']);
const accountConversationMetric = computed(
  () => store.getters.getAccountConversationMetric
);
const openConversationsCount = computed(
  () => accountConversationMetric.value.open || 0
);

// Soma (não por time) das conversas aguardando resposta de agente
// (waiting_since presente) de todos os times — usado como o "cheio" da
// barra da fila, pra a barra representar a fatia dos times selecionados
// dentro do total (sem filtro no header = barra cheia).
const filaTotal = computed(() =>
  teamConversationMetric.value.reduce(
    (sum, metric) => sum + (metric.waiting || 0),
    0
  )
);

// Mesma soma, mas só dos times selecionados no seletor global do header —
// não é só "aberto", senão contaria também quem já foi respondido e a bola
// tá com o cliente. Nenhum time selecionado (padrão) significa "sem
// filtro", então é igual ao total.
const filaAtual = computed(() => {
  const metrics = props.teamIds.length
    ? teamConversationMetric.value.filter(metric =>
        props.teamIds.includes(metric.team_id)
      )
    : teamConversationMetric.value;
  return metrics.reduce((sum, metric) => sum + (metric.waiting || 0), 0);
});

const labelName = label =>
  label.id === NO_LABEL_ID
    ? t('REPORT_DASHBOARD.OPERATIONAL.NO_LABEL')
    : label.name;

const allLabels = computed(() =>
  labelSummaryReports.value.map(label => ({
    id: label.id,
    name: labelName(label),
  }))
);
const allAgents = computed(() =>
  agents.value.map(agent => ({ id: agent.id, name: agent.name }))
);

const sortedLabels = computed(() =>
  labelSummaryReports.value
    .map(label => ({
      id: label.id,
      modalidade: labelName(label),
      quantidade: label.resolvedConversationsCount || 0,
    }))
    .sort((a, b) => b.quantidade - a.quantidade)
);

const sortedOpenLabels = computed(() =>
  labelSummaryReports.value
    .map(label => ({
      id: label.id,
      modalidade: labelName(label),
      quantidade: label.openConversationsCount || 0,
    }))
    .sort((a, b) => b.quantidade - a.quantidade)
);

const sortedAgents = computed(() =>
  agentSummaryReports.value
    .map(entry => {
      const agent = agents.value.find(item => item.id === entry.id);
      return {
        id: entry.id,
        colaborador: agent?.name || `Agente ${entry.id}`,
        quantidade: entry.resolvedConversationsCount || 0,
      };
    })
    .sort((a, b) => b.quantidade - a.quantidade)
);

const modalidades = computed(() =>
  sortedLabels.value.filter(item => selectedLabelIds.value.includes(item.id))
);

const modalidadesAbertas = computed(() =>
  sortedOpenLabels.value.filter(item =>
    selectedOpenLabelIds.value.includes(item.id)
  )
);

const producaoIndividual = computed(() =>
  sortedAgents.value.filter(item => selectedAgentIds.value.includes(item.id))
);

// Assim que os dados chegam pela primeira vez, marca os 5 primeiros como
// selecionados por padrão — depois disso, só a escolha manual do usuário
// no filtro altera a seleção.
watch(
  sortedLabels,
  list => {
    if (!labelDefaultsApplied.value && list.length) {
      selectedLabelIds.value = list
        .slice(0, DEFAULT_ITEMS_LIMIT)
        .map(item => item.id);
      labelDefaultsApplied.value = true;
    }
  },
  { immediate: true }
);

watch(
  sortedOpenLabels,
  list => {
    if (!openLabelDefaultsApplied.value && list.length) {
      selectedOpenLabelIds.value = list
        .slice(0, DEFAULT_ITEMS_LIMIT)
        .map(item => item.id);
      openLabelDefaultsApplied.value = true;
    }
  },
  { immediate: true }
);

watch(
  sortedAgents,
  list => {
    if (!agentDefaultsApplied.value && list.length) {
      selectedAgentIds.value = list
        .slice(0, DEFAULT_ITEMS_LIMIT)
        .map(item => item.id);
      agentDefaultsApplied.value = true;
    }
  },
  { immediate: true }
);

const modalidadesMax = computed(() =>
  Math.max(1, ...modalidades.value.map(item => item.quantidade))
);
const modalidadesAbertasMax = computed(() =>
  Math.max(1, ...modalidadesAbertas.value.map(item => item.quantidade))
);
const producaoMax = computed(() =>
  Math.max(1, ...producaoIndividual.value.map(item => item.quantidade))
);

const fetchWaitingConversations = async () => {
  const { data } = await liveReports.getWaitingConversations({
    inbox_id: props.inboxId,
    team_id: props.teamIds,
  });
  waitingToday.value = data.today;
  waitingYesterday.value = data.yesterday;
};

const RANGE_SECONDS = {
  '7d': 7 * SECONDS_IN_DAY,
  '30d': 30 * SECONDS_IN_DAY,
};

// "24h" aqui significa "hoje" (desde a meia-noite), não uma janela rolante
// das últimas 24 horas — pra bater com o resto da tela (ex: "Clientes
// atendidos no dia" e o total de "Atendimentos finalizados" do Central, que
// já usam meia-noite). Antes esse selector usava janela rolante e o total
// somado por colaborador não batia com o total mostrado no Central.
const rangeFor = key => {
  const until = Math.floor(Date.now() / 1000);
  if (key === '24h') return { since: getUnixStartOfDay(new Date()), until };
  return { since: until - RANGE_SECONDS[key], until };
};

// Sem filtro de tag, busca via summary_reports/team (mesmo endpoint usado em
// fetchResolvedConversationsToday) pra poder filtrar/combinar pelos times
// selecionados no seletor do header — reports/summary só aceita um time por
// vez (type: team, id: <team_id>), o que não dá pra usar com multi-seleção.
// Com filtro de tag, troca pra summary_reports/agent com label_id (mesmo
// usado no TeamDashboard) — o backend filtra as tags e já deduplica, então
// uma conversa com mais de uma das tags selecionadas conta uma vez só. Somar
// por etiqueta (summary_reports/label) contaria essa conversa uma vez por tag
// e a média com "Marcar todos" não bateria com a média sem filtro.
const fetchAvgResolutionTimes = async () => {
  const until = Math.floor(Date.now() / 1000);
  const since24h = until - SECONDS_IN_DAY;
  const since7Days = until - 7 * SECONDS_IN_DAY;
  const hasLabelFilter = selectedAvgTimeLabelIds.value.length > 0;

  const fetchWindow = since =>
    hasLabelFilter
      ? summaryReportsApi.getAgentReports({
          since,
          until,
          inboxId: props.inboxId,
          teamId: props.teamIds,
          labelId: selectedAvgTimeLabelIds.value,
        })
      : summaryReportsApi.getTeamReports({
          since,
          until,
          inboxId: props.inboxId,
        });

  const [last24h, last7Days] = await Promise.all([
    fetchWindow(since24h),
    fetchWindow(since7Days),
  ]);

  const filterEntries = data =>
    hasLabelFilter ? data : filterByIds(data, props.teamIds);

  avgResolutionTime24h.value = formatFriendlyDuration(
    weightedAvgResolutionTime(filterEntries(last24h.data))
  );
  avgResolutionTimeWeekly.value = formatFriendlyDuration(
    weightedAvgResolutionTime(filterEntries(last7Days.data))
  );
};

// Chamado direto pela API (sem passar pelo Vuex `summaryReports`) porque
// aquele state guarda um único resultado por vez e é compartilhado com o
// card "Produção individual" — se usássemos o mesmo state, trocar o período
// ali (7d/30d) também mudaria esse total, que deve ficar sempre no dia
// corrente (desde a meia-noite), somado entre todos os usuários.
// Agrupado por time (não por agente) pra poder respeitar o filtro de time do
// header — o endpoint já devolve um item por time mais um item "sem time"
// (id 0) quando existem conversas sem time no período.
const fetchResolvedConversationsToday = async () => {
  const since = getUnixStartOfDay(new Date());
  const until = Math.floor(Date.now() / 1000);
  const { data } = await summaryReportsApi.getTeamReports({
    since,
    until,
    inboxId: props.inboxId,
  });
  totalFinalizadosHoje.value = filterByIds(data, props.teamIds).reduce(
    (sum, entry) => sum + (entry.resolved_conversations_count || 0),
    0
  );
};

const fetchLabelReports = () =>
  store.dispatch('summaryReports/fetchLabelSummaryReports', {
    ...rangeFor(labelRange.value),
    inboxId: props.inboxId,
    teamId: props.teamIds,
    includeUnlabeled: true,
  });

const fetchAgentReports = () =>
  store.dispatch('summaryReports/fetchAgentSummaryReports', {
    ...rangeFor(agentRange.value),
    inboxId: props.inboxId,
    teamId: props.teamIds,
  });

const fetchAccountConversationMetric = () =>
  store.dispatch('fetchAccountConversationMetric', {
    inbox_id: props.inboxId,
    team_id: props.teamIds,
  });

// Chamado direto pela API (sem passar pelo Vuex `fetchTeamConversationMetric`)
// porque aquela action engole qualquer erro silenciosamente (catch vazio) —
// aqui, se a chamada falhar, o erro aparece no console em vez de sumir.
const fetchTeamConversationMetric = async () => {
  const { data } = await liveReports.getGroupedConversations({
    groupBy: 'team_id',
    inboxId: props.inboxId,
  });
  // O backend agrupa pela coluna team_id crua, então conversas sem time
  // vêm com team_id null — normaliza pra NO_TEAM_ID (0) pra bater com a
  // opção "sem time" do seletor global do header.
  teamConversationMetric.value = data.map(metric => ({
    ...metric,
    team_id: metric.team_id ?? NO_TEAM_ID,
  }));
};

const refreshLiveData = () =>
  Promise.all([
    fetchWaitingConversations(),
    fetchLabelReports(),
    fetchAgentReports(),
    fetchAccountConversationMetric(),
    fetchAvgResolutionTimes(),
    fetchTeamConversationMetric(),
    fetchResolvedConversationsToday(),
  ]);

// Rebusca na hora quando o período selecionado muda, sem esperar o
// próximo ciclo de 60s do useLiveRefresh.
watch(agentRange, fetchAgentReports);
watch(labelRange, fetchLabelReports);
watch(selectedAvgTimeLabelIds, fetchAvgResolutionTimes, { deep: true });
watch(() => props.inboxId, refreshLiveData);
watch(
  () => props.teamIds,
  () =>
    Promise.all([
      fetchWaitingConversations(),
      fetchAccountConversationMetric(),
      fetchResolvedConversationsToday(),
      fetchAvgResolutionTimes(),
      fetchAgentReports(),
      fetchLabelReports(),
    ]),
  { deep: true }
);

const { startRefetching } = useLiveRefresh(refreshLiveData);

onMounted(() => {
  store.dispatch('agents/get');
  refreshLiveData();
  startRefetching();
});
</script>

<template>
  <section
    class="relative h-full flex flex-col px-6 lg:px-16 py-6 overflow-hidden"
  >
    <div
      class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 flex-1 min-h-0 items-stretch"
    >
      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 overflow-y-auto no-scrollbar"
      >
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.OPERATIONAL.WAITING_TODAY')"
          :value="waitingToday"
          :hint="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.WAITING_TODAY')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.OPERATIONAL.WAITING_YESTERDAY')"
          :value="waitingYesterday"
          :hint="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.WAITING_YESTERDAY')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.OPERATIONAL.IN_PROGRESS')"
          :value="openConversationsCount"
          :hint="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.IN_PROGRESS')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.OPERATIONAL.RESOLVED_CONVERSATIONS')"
          :value="totalFinalizadosHoje"
          :hint="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.RESOLVED_CONVERSATIONS')"
        />
      </div>

      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 overflow-y-auto no-scrollbar"
      >
        <div class="flex items-center justify-end mb-2 shrink-0">
          <MultiSelectFilter
            v-model="selectedAvgTimeLabelIds"
            :items="allLabels"
            :placeholder="t('REPORT_DASHBOARD.OPERATIONAL.ALL_LABELS')"
            :empty-state-message="
              t('REPORT_DASHBOARD.OPERATIONAL.NO_LABELS_FOUND')
            "
            show-select-all
            align-right
          />
        </div>
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.OPERATIONAL.AVG_TIME_24H')"
          :value="avgResolutionTime24h"
          :hint="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.AVG_TIME_24H')"
        />
        <LabelValueRow
          :label="t('REPORT_DASHBOARD.OPERATIONAL.AVG_TIME_WEEKLY')"
          :value="avgResolutionTimeWeekly"
          :hint="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.AVG_TIME_WEEKLY')"
        />
      </div>

      <div class="border border-n-weak rounded-lg p-5 min-w-0">
        <h2
          v-tooltip="t('REPORT_DASHBOARD.OPERATIONAL.HINTS.CURRENT_QUEUE')"
          class="text-xs uppercase tracking-widest text-n-slate-11 mb-3"
        >
          {{ t('REPORT_DASHBOARD.OPERATIONAL.CURRENT_QUEUE') }}
        </h2>
        <QueueGauge :current="filaAtual" :max="filaTotal" />
      </div>

      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 flex flex-col overflow-hidden"
      >
        <div class="flex items-center justify-between mb-2 shrink-0">
          <h2
            v-tooltip="
              t('REPORT_DASHBOARD.OPERATIONAL.HINTS.INDIVIDUAL_PRODUCTION')
            "
            class="text-xs uppercase tracking-widest text-n-slate-11"
          >
            {{ t('REPORT_DASHBOARD.OPERATIONAL.INDIVIDUAL_PRODUCTION') }}
          </h2>
          <MultiSelectFilter
            v-model="selectedAgentIds"
            :items="allAgents"
            :placeholder="t('REPORT_DASHBOARD.OPERATIONAL.ALL_AGENTS')"
            :empty-state-message="
              t('REPORT_DASHBOARD.OPERATIONAL.NO_AGENTS_FOUND')
            "
            show-select-all
            align-right
          />
        </div>
        <TimeRangeSelector v-model="agentRange" class="mb-3 shrink-0" />
        <div class="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          <BlockBar
            v-for="item in producaoIndividual"
            :key="item.colaborador"
            :label="item.colaborador"
            :value="item.quantidade"
            :max="producaoMax"
          />
          <p
            v-if="!producaoIndividual.length"
            class="text-sm text-n-slate-11 py-1"
          >
            {{ t('REPORT_DASHBOARD.OPERATIONAL.NO_AGENTS_FOUND') }}
          </p>
        </div>
      </div>

      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 flex flex-col overflow-hidden"
      >
        <div class="flex items-center justify-between mb-2 shrink-0">
          <h2
            v-tooltip="
              t('REPORT_DASHBOARD.OPERATIONAL.HINTS.CONVERSATIONS_BY_LABEL')
            "
            class="text-xs uppercase tracking-widest text-n-slate-11"
          >
            {{ t('REPORT_DASHBOARD.OPERATIONAL.CONVERSATIONS_BY_LABEL') }}
          </h2>
          <MultiSelectFilter
            v-model="selectedLabelIds"
            :items="allLabels"
            :placeholder="t('REPORT_DASHBOARD.OPERATIONAL.ALL_LABELS')"
            :empty-state-message="
              t('REPORT_DASHBOARD.OPERATIONAL.NO_LABELS_FOUND')
            "
            show-select-all
            align-right
          />
        </div>
        <TimeRangeSelector v-model="labelRange" class="mb-3 shrink-0" />
        <div class="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          <BlockBar
            v-for="item in modalidades"
            :key="item.modalidade"
            :label="item.modalidade"
            :value="item.quantidade"
            :max="modalidadesMax"
          />
          <p v-if="!modalidades.length" class="text-sm text-n-slate-11 py-1">
            {{ t('REPORT_DASHBOARD.OPERATIONAL.NO_LABEL_CONVERSATIONS') }}
          </p>
        </div>
      </div>

      <div
        class="border border-n-weak rounded-lg p-5 min-w-0 flex flex-col overflow-hidden"
      >
        <div class="flex items-center justify-between mb-2 shrink-0">
          <h2
            v-tooltip="
              t(
                'REPORT_DASHBOARD.OPERATIONAL.HINTS.OPEN_CONVERSATIONS_BY_LABEL'
              )
            "
            class="text-xs uppercase tracking-widest text-n-slate-11"
          >
            {{ t('REPORT_DASHBOARD.OPERATIONAL.OPEN_CONVERSATIONS_BY_LABEL') }}
          </h2>
          <MultiSelectFilter
            v-model="selectedOpenLabelIds"
            :items="allLabels"
            :placeholder="t('REPORT_DASHBOARD.OPERATIONAL.ALL_LABELS')"
            :empty-state-message="
              t('REPORT_DASHBOARD.OPERATIONAL.NO_LABELS_FOUND')
            "
            show-select-all
            align-right
          />
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto no-scrollbar">
          <BlockBar
            v-for="item in modalidadesAbertas"
            :key="item.modalidade"
            :label="item.modalidade"
            :value="item.quantidade"
            :max="modalidadesAbertasMax"
          />
          <p
            v-if="!modalidadesAbertas.length"
            class="text-sm text-n-slate-11 py-1"
          >
            {{ t('REPORT_DASHBOARD.OPERATIONAL.NO_OPEN_LABEL_CONVERSATIONS') }}
          </p>
        </div>
      </div>
    </div>

    <div
      class="absolute bottom-6 right-8 grid place-content-center size-9 bg-white rounded-lg"
    >
      <Logo class="size-5" />
    </div>
  </section>
</template>
